import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import { Button } from '../../ui';

interface MarketingCaptureConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

export const MarketingCaptureConfig: React.FC<MarketingCaptureConfigProps> = ({
  config,
  onChange,
}) => {
  const { bitrixFields } = useFlowBuilderStore();

  const targetEntity = config.targetEntity === 'deal' ? 'deal' : 'lead';
  const matchMode = config.matchMode === 'exact' ? 'exact' : 'contains';

  const entityFields = (bitrixFields?.[targetEntity] || {}) as Record<string, any>;

  const sourceFieldOptions = useMemo(() => {
    return Object.entries(entityFields).map(([fieldId, field]: [string, any]) => ({
      value: fieldId,
      label: field.formLabel || field.title || fieldId,
      type: field.type,
      items: Array.isArray(field.items) ? field.items : [],
    }));
  }, [entityFields]);

  const sourceFieldId = config.sourceFieldId || 'SOURCE_ID';
  const selectedSourceField = sourceFieldOptions.find((f) => f.value === sourceFieldId);

  const sourceValueOptions = useMemo(() => {
    if (!selectedSourceField?.items?.length) return [];

    return selectedSourceField.items.map((item: any) => {
      const value = String(item.ID ?? item.VALUE ?? '');
      const label = String(item.VALUE ?? item.NAME ?? item.ID ?? '');
      return { value, label };
    }).filter((item: any) => item.value);
  }, [selectedSourceField]);

  const dealCategories = Array.isArray(bitrixFields?.dealCategories)
    ? bitrixFields.dealCategories
    : [];

  const rules = Array.isArray(config.rules) ? config.rules : [];

  const updateUtmFieldMap = (key: string, value: string) => {
    onChange('utmFieldMap', {
      ...(config.utmFieldMap || {}),
      [key]: value,
    });
  };

  const updateRule = (index: number, key: string, value: string) => {
    const next = [...rules];
    next[index] = { ...next[index], [key]: value };
    onChange('rules', next);
  };

  const addRule = () => {
    onChange('rules', [
      ...rules,
      {
        id: crypto.randomUUID(),
        code: '',
        sourceValue: '',
        utmSource: '',
        utmMedium: '',
        utmCampaign: '',
        utmContent: '',
        utmTerm: '',
      },
    ]);
  };

  const removeRule = (index: number) => {
    const next = [...rules];
    next.splice(index, 1);
    onChange('rules', next);
  };

  return (
    <div className="space-y-4">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700 leading-relaxed">
        <strong>Como funciona:</strong> em toda nova mensagem do cliente, o sistema procura um código/palavra cadastrado abaixo. Quando encontrar, ele busca pelo telefone do cliente e atualiza a origem + UTM no Lead ou no Negócio.
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Buscar em</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            value={targetEntity}
            onChange={(e) => onChange('targetEntity', e.target.value === 'deal' ? 'deal' : 'lead')}
          >
            <option value="lead">Lead</option>
            <option value="deal">Negócio</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Tipo de leitura do código</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            value={matchMode}
            onChange={(e) => onChange('matchMode', e.target.value === 'exact' ? 'exact' : 'contains')}
          >
            <option value="contains">Contém o código na frase</option>
            <option value="exact">Frase igual ao código</option>
          </select>
        </div>
      </div>

      {targetEntity === 'deal' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Pipeline do Negócio</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            value={config.dealPipelineId ?? 'all'}
            onChange={(e) => onChange('dealPipelineId', e.target.value)}
          >
            <option value="all">Todos os pipelines</option>
            {dealCategories.map((category: any) => (
              <option key={String(category.ID)} value={String(category.ID)}>
                {category.NAME || `Pipeline ${category.ID}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Telefone para busca</label>
        <input
          type="text"
          className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-md"
          value={config.phoneVariable ?? '{{sender_phone}}'}
          onChange={(e) => onChange('phoneVariable', e.target.value)}
          placeholder="{{sender_phone}}"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Campo de Origem (lista)</label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            value={sourceFieldId}
            onChange={(e) => onChange('sourceFieldId', e.target.value)}
          >
            <option value="SOURCE_ID">Origem padrão (SOURCE_ID)</option>
            {sourceFieldOptions.map((field) => (
              <option key={field.value} value={field.value}>
                {field.label} ({field.value})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={config.listenEveryMessage !== false}
              onChange={(e) => onChange('listenEveryMessage', e.target.checked)}
            />
            Capturar em toda mensagem
          </label>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg p-3 space-y-3">
        <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wide">Mapeamento UTM</h4>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            placeholder="Campo UTM Source (ex: UTM_SOURCE)"
            value={config.utmFieldMap?.source || ''}
            onChange={(e) => updateUtmFieldMap('source', e.target.value)}
          />
          <input
            type="text"
            className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            placeholder="Campo UTM Medium"
            value={config.utmFieldMap?.medium || ''}
            onChange={(e) => updateUtmFieldMap('medium', e.target.value)}
          />
          <input
            type="text"
            className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            placeholder="Campo UTM Campaign"
            value={config.utmFieldMap?.campaign || ''}
            onChange={(e) => updateUtmFieldMap('campaign', e.target.value)}
          />
          <input
            type="text"
            className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
            placeholder="Campo UTM Content"
            value={config.utmFieldMap?.content || ''}
            onChange={(e) => updateUtmFieldMap('content', e.target.value)}
          />
          <input
            type="text"
            className="px-2 py-1.5 text-xs border border-slate-300 rounded-md col-span-2"
            placeholder="Campo UTM Term"
            value={config.utmFieldMap?.term || ''}
            onChange={(e) => updateUtmFieldMap('term', e.target.value)}
          />
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wide">Códigos e Destino</h4>
          <Button variant="outline" size="sm" onClick={addRule}>
            <Plus className="w-3 h-3 mr-1" />
            Regra
          </Button>
        </div>

        {rules.length === 0 ? (
          <p className="text-xs text-slate-500">Adicione pelo menos uma regra de código.</p>
        ) : (
          <div className="space-y-3">
            {rules.map((rule: any, index: number) => (
              <div key={rule.id || index} className="border border-slate-200 rounded-md p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                    placeholder="Código ou palavra"
                    value={rule.code || ''}
                    onChange={(e) => updateRule(index, 'code', e.target.value)}
                  />

                  {sourceValueOptions.length > 0 ? (
                    <select
                      className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                      value={rule.sourceValue || ''}
                      onChange={(e) => updateRule(index, 'sourceValue', e.target.value)}
                    >
                      <option value="">Selecione a origem</option>
                      {sourceValueOptions.map((item: { value: string; label: string }) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                      placeholder="Valor da origem"
                      value={rule.sourceValue || ''}
                      onChange={(e) => updateRule(index, 'sourceValue', e.target.value)}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                    placeholder="UTM Source"
                    value={rule.utmSource || ''}
                    onChange={(e) => updateRule(index, 'utmSource', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                    placeholder="UTM Medium"
                    value={rule.utmMedium || ''}
                    onChange={(e) => updateRule(index, 'utmMedium', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                    placeholder="UTM Campaign"
                    value={rule.utmCampaign || ''}
                    onChange={(e) => updateRule(index, 'utmCampaign', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-2 py-1.5 text-xs border border-slate-300 rounded-md"
                    placeholder="UTM Content"
                    value={rule.utmContent || ''}
                    onChange={(e) => updateRule(index, 'utmContent', e.target.value)}
                  />
                  <input
                    type="text"
                    className="px-2 py-1.5 text-xs border border-slate-300 rounded-md col-span-2"
                    placeholder="UTM Term"
                    value={rule.utmTerm || ''}
                    onChange={(e) => updateRule(index, 'utmTerm', e.target.value)}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                    onClick={() => removeRule(index)}
                  >
                    <Trash2 className="w-3 h-3" />
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
